import { useTranslation } from 'react-i18next';
import { StationRequestsFilterBar } from './StationRequestsFilterBar';
import { StationRequestsTable } from './StationRequestsTable';
import { StationRequestsTableSkeleton } from './StationRequestsTableSkeleton';
import { MobileStationRequestCard } from './MobileStationRequestCard';
import { MobileCardSkeleton } from '../../../components/common/MobileCardSkeleton/MobileCardSkeleton';
import { ListPageLayout } from '../components/ListPageLayout';
import { StationRequestsFilterState } from '../../../types/dashboardV2';
import { useStationRequestsQuery } from '../../../hooks/useDashboardQueries';
import { useListPage } from '../../../hooks/useListPage';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import { SUPPORT_SECTION } from '../../../layouts/DashboardV2Layout/dashboardSections';

const INITIAL_FILTERS: StationRequestsFilterState = { search: '' };

export const StationRequestsV2 = () => {
  const { t } = useTranslation();

  const {
    page,
    rowsPerPage,
    filters,
    queryFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    refresh: handleRefresh,
  } = useListPage<StationRequestsFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.STATION_REQUESTS,
    urlSync: true,
  });

  const { data, isLoading: loading, isFetching, error } =
    useStationRequestsQuery({ page: page + 1, pageSize: rowsPerPage, filters: queryFilters });

  const requests = data?.data ?? [];
  const total = data?.total ?? 0;
  const errorMessage = error instanceof Error ? error.message : t('dashboard.stationRequests.errorLoading');

  return (
    <ListPageLayout
      section={SUPPORT_SECTION}
      filterBar={
        <StationRequestsFilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          allRequests={requests}
        />
      }
      showLoading={loading || isFetching}
      isLoading={loading}
      hasError={Boolean(error)}
      errorMessage={errorMessage}
      onRetry={handleRefresh}
      countText={t('dashboard.stationRequests.displayingOfTotal', { count: requests.length, total })}
      itemCount={requests.length}
      emptyState={{ icon: 'add_location_alt', message: t('dashboard.stationRequests.noRequests') }}
      table={
        <StationRequestsTable
          requests={requests}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      }
      tableSkeleton={<StationRequestsTableSkeleton />}
      mobileTitle={t('dashboard.sidebar.supportSection')}
      mobileCountText={t('dashboard.stationRequests.allWithCount', { count: total })}
      search={{
        value: filters.search,
        onChange: (search) => handleFilterChange({ search }),
        placeholder: t('dashboard.stationRequests.searchPlaceholder'),
      }}
      mobileCards={requests.map((request) => (
        <MobileStationRequestCard key={request.id} request={request} />
      ))}
      mobileSkeleton={<MobileCardSkeleton showFooter={false} />}
      pagination={{
        total,
        page,
        rowsPerPage,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange,
      }}
    />
  );
};

export default StationRequestsV2;
